<template>
  <ErrorBoundary :errors="errors">
    <component :is="layout" v-if="ready" />
  </ErrorBoundary>

  <Loading :loading="loading" />
</template>

<script setup lang="ts">
import { useGtm } from '@gtm-support/vue-gtm';
import * as Sentry from '@sentry/vue';
import { computed, onMounted, provide, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import ErrorBoundary from '@/components/organisms/ErrorBoundary.vue';
import { useFlutterConnection } from '@/composables/useFlutterConnection';
import { useGlobalStore, GlobalStoreKey } from '@/composables/useGlobalStore';
import { MyListKey, useMyList } from '@/composables/useMyList';
import useProgramWorker from '@/composables/useProgramWorker?worker';
import { useSWRV } from '@/composables/useSWRV';
import useSearchWorker from '@/composables/useSearchWorker?worker';
import { RepositoryFactory } from '@/repositories/RepositoryFactory';
import Loading from './components/molecules/Loading/Loading.vue';
import { BlackListKey, useBlacklist } from './composables/useBlacklist';
import { useProgramModal, ProgramModalKey } from './composables/useProgramModal';
import { programWorkerDataKey, useProgramWorkerData } from './composables/useProgramWorkerData';
import { ScheduleKey, useSchedule } from './composables/useSchedule';
import { BlacklistRepository } from './repositories/blackProgramsRepository';
import apiAxiosRepository from './repositories/client/ApiAxiosClient';
import { MylistRepository } from './repositories/mylistRepository';
import { UserRepository } from './repositories/userRepository';

const APP_ENVIRONMENT = import.meta.env.VITE_APP_ENVIRONMENT;

const route = useRoute();
const router = useRouter();
const gtm = useGtm();
const defaultLayout = 'default';
const layout = computed(() => (route.meta.layout || defaultLayout) + '-layout');

// const { getUserId, setUserId: setUserIdToApp, registerToken, getAuth, homeIsLoadedNotify } = useFlutterConnection();
const { getUserId, setUserId: setUserIdToApp, registerToken, getAuth } = useFlutterConnection();
const globalStore = useGlobalStore();
provide(GlobalStoreKey, globalStore);
const {
  setUserId,
  loading,
  setIsFirstLogin,
  setUserAgent,
  setUserAgentData,
  setSearchWorker,
  setProgramWorker,
  startLoading,
  stopLoading,
} = globalStore;

provide(ProgramModalKey, useProgramModal());

const ready = ref(false);

const userAgent = window.navigator.userAgent;
const userAgentData = window.navigator?.userAgentData;
if (userAgent) setUserAgent(userAgent);
if (userAgentData) setUserAgentData(userAgentData);

const myListRepository = RepositoryFactory.get('mylist') as MylistRepository;

const myList = useMyList();
provide(MyListKey, myList);
const { setMyListData } = myList;

const scheduleProvider = useSchedule();
provide(ScheduleKey, scheduleProvider);

const programWorkerData = useProgramWorkerData();
provide(programWorkerDataKey, programWorkerData);
const { setProgramWorkerDatum } = programWorkerData;

// ブラックリストの登録
const { getMap } = RepositoryFactory.get('blacklist') as BlacklistRepository;
const { data, error, mutate } = useSWRV('/blackList', getMap);
const blackList = useBlacklist();
provide(BlackListKey, blackList);
const { setBlacklist } = blackList;

watch(
  data,
  () => {
    if (data.value) setBlacklist(data.value);
  },
  { immediate: true }
);
watch(error, (e) => {
  errors.value.push(e);
});
/**
 * ブラックリストのデータを更新する
 * その他・ニュース・アンケートページは更新しない
 */
watch(
  () => route.path,
  (to) => {
    if (to === '/other' || to === '/news' || to === '/survey') return;
    mutate();
  }
);

const errors = ref<Error[]>([]);

onMounted(async () => {
  startLoading();
  try {
    // userIdの解決
    const userId = await getUserId();

    // apiClientのinterceptでheaderに認証キーをセット
    const authorizationKey = (await getAuth()) || '';
    if (authorizationKey === '') throw new Error('failed to get AuthorizationKey');
    apiAxiosRepository.interceptors.request.use((config) => {
      if (config.headers) config.headers['Authorization'] = authorizationKey;
      return config;
    });

    // Sentryにユーザー情報を登録
    Sentry.setUser({ id: String(userId), environment: APP_ENVIRONMENT });

    // 検索機能用ワーカーセットアップ
    const searchWorker = new useSearchWorker();
    searchWorker.onmessage = (e) => {
      if (e.data.type === 'error') {
        const error = new Error(e.data.error);
        error.name = e.data.name;
        error.stack = e.data.stack;
        errors.value.push(error);
      }
    };
    setSearchWorker(searchWorker);

    // 番組用ワーカーセットアップ
    const programWorker = new useProgramWorker();
    programWorker.onmessage = (e) => {
      if (e.data.type === 'error') {
        const error = new Error(e.data.error);
        error.name = e.data.name;
        error.stack = e.data.stack;
        errors.value.push(error);
      }
      setProgramWorkerDatum(e.data);
    };
    setProgramWorker(programWorker);

    // userIdがある場合
    if (userId != null) {
      await _resolveUserCallback(userId);
      setIsFirstLogin(false);

      // マイリストデータセットアップ
      const items = await myListRepository.getAllProgramsMap(userId);
      setMyListData(items);

      // HOME起動画面時に都度FCMトークンを更新する
      registerToken(userId as number);

      return;
    }

    // userIdがない場合
    else {
      const { create } = RepositoryFactory.get('user') as UserRepository;
      const { id } = await create();
      if (import.meta.env.MODE !== 'prod') {
        console.debug(`作成されたuserId:${id}`);
      }
      await setUserIdToApp(id);
      await _resolveUserCallback(id);
      setIsFirstLogin(true);
      router.push('/survey');
      // ユーザーの作成後にFCMトークンを更新する
      registerToken(id);
    }
  } catch (e: any) {
    errors.value.push(e);
  }
});

/**
 * userId解決後に実行するコールバック関数
 * @param userId
 */
const _resolveUserCallback = async (userId: number) => {
  setUserId(userId);
  gtm?.trackEvent({ uid: userId });

  // Sentryにユーザー情報を登録
  Sentry.setUser({ id: String(userId), environment: APP_ENVIRONMENT });

  stopLoading();
  ready.value = true; // NOTE: UserIDが解決してからUI展開する
};
</script>
